<script setup lang="ts">
import type { IImportCellCorrection } from '../types/IImportCellCorrection'
import type { IImportPreviewRow } from '../types/IImportPreviewRow'

const props = defineProps<{
  node: IImportPreviewRow
  /** Сопоставленные колонки схемы. В строке показываем только их непустые тексты. */
  columns: string[]
  depth?: number
  collapsed: string[]
  allowCellEdit: boolean
  editingKey: string | null
  editingValue: string
}>()

const emit = defineEmits<{
  toggle: [key: string]
  'start-edit': [payload: { key: string, value: string }]
  'update:editingValue': [value: string]
  save: [payload: IImportCellCorrection]
  cancel: []
}>()

const lockedColumns = new Set(['Путь', '№'])

const depth = computed(() => props.depth ?? 0)
const nodeKey = computed(() => props.node.path || String(props.node.row ?? props.node.name ?? ''))
const hasChildren = computed(() => (props.node.children?.length ?? 0) > 0)
const isCollapsed = computed(() => props.collapsed.includes(nodeKey.value))

function clean(value: string | null | undefined) {
  return value == null ? '' : String(value).replace(/\s+/g, ' ').trim()
}

const indexPattern = /^\d+(?:\.\d+)*\.?$/
const hiddenColumns = new Set(['№', 'Путь', 'Имя файла'])

interface ITreeField {
  key: string
  value: string
  number: string
}

function isNumberColumn(key: string) {
  return key === '№' || key.startsWith('№ ')
}

function numberText(value: string) {
  return value.replace(/\.$/, '')
}

/** Текст сопоставленной колонки и номер из соседней колонки «№» в том же data. */
const fields = computed<ITreeField[]>(() => {
  const name = clean(props.node.name)
  const path = clean(props.node.path)
  if (!indexPattern.test(name))
    return [{ key: '', value: name || path, number: '' }]

  const numberForKey = new Map<string, string>()
  let pendingNumber = ''
  for (const [key, raw] of Object.entries(props.node.data || {})) {
    if (isNumberColumn(key)) {
      pendingNumber = numberText(clean(raw))
      continue
    }
    if (pendingNumber) {
      numberForKey.set(key, pendingNumber)
      pendingNumber = ''
    }
  }

  const shortSuffix = ' (сокращенно)'
  const allowed = new Set(props.columns)
  const texts = Object.entries(props.node.data || {})
    .filter(([key, raw]) => allowed.has(key) && !hiddenColumns.has(key) && clean(raw))
    .map(([key, raw]) => ({ key, value: clean(raw) }))
  if (!texts.length)
    return [{ key: '', value: name, number: '' }]

  const byKey = new Map(texts.map(item => [item.key, item.value]))
  const used = new Set<string>()
  const ordered: ITreeField[] = []

  function pushField(key: string, withNumber: boolean) {
    const value = byKey.get(key)
    if (!value || used.has(key))
      return
    used.add(key)
    ordered.push({
      key,
      value,
      number: withNumber ? (numberForKey.get(key) || '') : '',
    })
  }

  for (const item of texts) {
    if (item.key.endsWith(shortSuffix))
      continue
    pushField(item.key, true)
    pushField(`${item.key}${shortSuffix}`, false)
  }
  for (const item of texts)
    pushField(item.key, false)

  return ordered
})

const editableErrors = computed(() =>
  props.allowCellEdit
    ? props.node.errors.filter(issue => issue.column && !lockedColumns.has(issue.column) && props.node.row != null)
    : [],
)

function issueKey(column: string) {
  return `${nodeKey.value}:${column}`
}

function isEditing(column: string) {
  return props.editingKey === issueKey(column)
}

function startEdit(column: string, value: string | null) {
  emit('start-edit', { key: issueKey(column), value: value == null ? '' : String(value) })
}

function saveEdit(column: string) {
  if (props.node.row == null)
    return
  emit('save', { row: props.node.row, column, value: props.editingValue })
}

function onErrorInput(el: unknown) {
  if (!(el instanceof HTMLInputElement))
    return
  el.focus()
  const end = el.value.length
  el.setSelectionRange(end, end)
}
</script>

<template>
  <div class="import-tree-node">
    <div
      class="import-tree-row d-flex align-start"
      :style="{ paddingInlineStart: `${12 + depth * 16}px` }"
    >
      <VIcon
        v-if="hasChildren"
        :icon="isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'"
        size="20"
        class="import-tree-icon flex-shrink-0 text-high-emphasis"
        @click="emit('toggle', nodeKey)"
      />
      <span v-else class="import-tree-placeholder" />
      <span class="body-1 text-high-emphasis import-tree-label d-flex flex-column ga-1">
        <span
          v-for="(field, index) in fields"
          :key="field.key || index"
          class="d-flex flex-wrap ga-2"
        >
          <span
            v-if="field.number"
            class="text-medium-emphasis"
          >{{ field.number }}</span>
          <span>
            <span
              v-if="field.key"
              class="text-medium-emphasis"
            >{{ field.key }}: </span>{{ field.value }}
          </span>
        </span>
      </span>
      <VIcon
        :icon="node.errors.length ? 'ri-alert-line' : 'ri-checkbox-circle-line'"
        :color="node.errors.length ? 'error' : 'success'"
        size="20"
        class="flex-shrink-0"
      />
    </div>
    <div
      v-for="issue in node.errors"
      :key="`${nodeKey}:${issue.column}`"
      class="import-tree-error d-flex align-center justify-space-between"
      :style="{ paddingInlineStart: `${12 + depth * 16 + 24}px` }"
    >
      <template v-if="editableErrors.some(item => item.column === issue.column)">
        <input
          v-if="isEditing(issue.column || '')"
          :ref="onErrorInput"
          :value="editingValue"
          class="import-tree-input text-error"
          @input="emit('update:editingValue', ($event.target as HTMLInputElement).value)"
          @keydown.enter.prevent="saveEdit(issue.column || '')"
        >
        <span v-else class="text-error">{{ issue.value }}</span>
        <div class="d-flex align-center">
          <template v-if="isEditing(issue.column || '')">
            <VTooltip location="top" text="Сохранить">
              <template #activator="{ props: tooltipProps }">
                <VIconBtn
                  v-bind="tooltipProps"
                  :rounded="false"
                  :size="32"
                  icon="ri-check-line"
                  variant="text"
                  @click="saveEdit(issue.column || '')"
                />
              </template>
            </VTooltip>
            <VTooltip location="top" text="Отменить">
              <template #activator="{ props: tooltipProps }">
                <VIconBtn
                  v-bind="tooltipProps"
                  :rounded="false"
                  :size="32"
                  icon="ri-close-line"
                  variant="text"
                  @click="emit('cancel')"
                />
              </template>
            </VTooltip>
          </template>
          <VIconBtn
            v-else
            :rounded="false"
            :size="32"
            icon="ri-pencil-line"
            variant="text"
            @mousedown.prevent
            @click="startEdit(issue.column || '', issue.value)"
          />
        </div>
      </template>
      <span v-else class="text-error text-body-2">{{ issue.message }}</span>
    </div>
    <template v-if="hasChildren && !isCollapsed">
      <ImportPreviewTreeNode
        v-for="child in node.children"
        :key="String(child.path || child.row || child.name || '')"
        :node="child"
        :columns="columns"
        :depth="depth + 1"
        :collapsed="collapsed"
        :allow-cell-edit="allowCellEdit"
        :editing-key="editingKey"
        :editing-value="editingValue"
        @toggle="emit('toggle', $event)"
        @start-edit="emit('start-edit', $event)"
        @update:editing-value="emit('update:editingValue', $event)"
        @save="emit('save', $event)"
        @cancel="emit('cancel')"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.import-tree-node {
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.import-tree-row {
  gap: 4px;
  min-inline-size: 0;
  padding-block: 8px;
  padding-inline-end: 12px;
}

.import-tree-label {
  flex: 1;
  min-inline-size: 0;
  overflow-wrap: anywhere;
}

.import-tree-placeholder {
  flex-shrink: 0;
  inline-size: 20px;
}

.import-tree-error {
  gap: 8px;
  padding-block: 4px;
  padding-inline-end: 12px;
}

.import-tree-input {
  background: transparent;
  border: 0;
  caret-color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  flex: 1 1 auto;
  font: inherit;
  min-inline-size: 0;
  outline: none;
}
</style>
